import React, {useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import I18n from 'app/i18n';
import {GAMER_1, GAMER_2, GREEN} from 'app/lines/line_helpers';
import {gameImage, scrollToTop} from 'app/utils';
import {showBetSlip} from 'app/components/table';
import {openStream} from 'app/components/streams';
import GamerPhoto from 'app/components/bet_form/GamerPhoto';
import {lineGamer} from 'app/lines/lineGamer';
import {hasUserBet} from 'app/lines/hasUserBet';
import {fixed3} from 'app/utils/fixed';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import QuickBetsTimer from 'app/components/pages/play_content/QuickBetsTimer';
import {TABLE_LABEL_TYPES_CUP, TABLE_LABEL_TYPES_FIRE} from 'app/consts';
import {ONE_SECOND_MS} from 'app/store/constants/storeConstants';
import {QuickBetLineModel} from 'app/store/QuickBetLineModel';
import {streamStore} from 'app/store/global';
import StreamInterface from 'app/interfaces/StreamInterface';

interface PreviewGamesItemQuickBetsProps {
    model: QuickBetLineModel;
    closed: boolean;
    gamerActiveCoef?: {on: number} | null;
}

// eslint-disable-next-line max-statements
const QuickBetsItem = observer(({model, closed: closedProp, gamerActiveCoef}: PreviewGamesItemQuickBetsProps): React.ReactElement => {
    const lineRef = useRef(model.raw);

    useEffect(() => {
        lineRef.current = model.raw;
    }, [model.id, model.quick_timer_end_at]);

    const dateInFuture = model.date > Date.now() / ONE_SECOND_MS;
    const hasQuickTimer = model.quick_timer_end_at > Date.now() / ONE_SECOND_MS;
    const effectiveClosed = !dateInFuture || !hasQuickTimer || closedProp;

    const colorClass = model.color === GREEN ? 'started' : '';

    const stream: StreamInterface = (() => {
        if (!model.streams_enabled || effectiveClosed) {
            return null;
        }

        const {streams} = streamStore;

        if (model.streams && model.streams.length > 0) {
            const [found] = streams
                .filter(s => model.streams.includes(s.id))
                .sort((a, b) => (b.twitch_channel ? 1 : 0) - (a.twitch_channel ? 1 : 0));

            if (found) {
                return found;
            }
        }

        const {tourn} = lineRef.current;

        if (tourn) {
            const [byTournament] = streams.filter(
                s => s.tournament_name &&
                    s.tournament_name.toLocaleLowerCase() === tourn.toLocaleLowerCase()
            );
            return byTournament ?? null;
        }
        return null;
    })();

    const handleCoefClick = (num: number): void => {
        if (effectiveClosed) {
            return;
        }
        showBetSlip(lineRef.current, num, {selectedBet: lineRef.current});
    };

    const handleOpenStream = (e: React.MouseEvent<HTMLOrSVGElement>): void => {
        e.stopPropagation();
        scrollToTop();
        if (stream) {
            openStream(stream);
        }
    };

    const renderCoef = (gamer: number): string => {
        const coef = gamer === GAMER_1 ? model.coef_1 : model.coef_2;
        return coef ? fixed3(coef.toString()) : '\u2015';
    };

    const isCoefActive = (gamer: number): boolean => Boolean(gamerActiveCoef && gamerActiveCoef.on === gamer);

    const coefButtonClasses = (gamer: number): string => {
        const placedBet = hasUserBet(model.raw, gamer) || model.placedGamer === gamer;
        const slipSelected = isCoefActive(gamer);

        let suffix = '';
        if (slipSelected) {
            suffix = ' button-preview-game-team--slip-active';
        } else if (placedBet) {
            suffix = ' button-preview-game-team--placed';
        }

        return `button-preview-game-team ${colorClass}${suffix}`;
    };

    const renderLabelIcon = (): React.ReactNode => {
        const {label_type} = lineRef.current;

        if (label_type !== 0 && label_type !== 1) {
            return null;
        }

        const src = label_type === 1 ? TABLE_LABEL_TYPES_CUP : TABLE_LABEL_TYPES_FIRE;

        return <img src={src} className="preview-game__label-icon" />;
    };

    const renderMiddleContent = (): React.ReactNode => {
        if (effectiveClosed) {
            return (
                <div className="preview-game__closed">
                    <span className="preview-game__closed-text">{I18n.t('closed')}</span>
                    <SpriteIcon name="#svg-lock" className="preview-game__closed-icon"/>
                </div>
            );
        }

        return (
            <div className="preview-game__timer-block">
                <QuickBetsTimer
                    lineDate={model.quick_timer_end_at}
                />
                {stream && <button
                    className="preview-game-watch-stream"
                    onClick={handleOpenStream}
                    title={I18n.t('live_stream_title')}
                >
                    <SpriteIcon name="#svg-movie" className="svg-default"/>
                    <span className="preview-game-watch-stream__title">{I18n.t('live_label')}</span>
                </button>}
            </div>
        );
    };

    const line = lineRef.current;
    const containerClass = `preview-game preview-game--quick-bets${effectiveClosed ? ' preview-game--closed' : ''}`;

    return (
        <div className={containerClass}>
            <div className="preview-game__heading">
                {renderLabelIcon()}
                <span className="preview-game__heading-title">{line.tourn}</span>
            </div>
            {line.game_label && <div className="quick-bets__question">
                <span className="quick-bets__question-text">
                    {line.game_label}
                </span>
            </div>}
            <div className="preview-game__body">
                <div className="preview-game__body-left">
                    <div className="preview-game-team">
                        <span className="preview-game-team-span-img">
                            <GamerPhoto
                                line_gamer={lineGamer(line, GAMER_1)}
                                className="preview-game-team-img"
                            />
                        </span>
                        <span className={`preview-game-team-name ${colorClass}`}>
                            <b>{lineGamer(line, GAMER_1)?.nick_quick}</b>
                        </span>
                        <button
                            className={coefButtonClasses(GAMER_1)}
                            onClick={() => handleCoefClick(GAMER_1)}
                        >
                            {renderCoef(GAMER_1)}
                        </button>
                    </div>
                </div>
                <div className="preview-game__body-middle">
                    <div className="preview-game-about">
                        {renderMiddleContent()}
                        {gameImage(line.filter, 'preview-game-logo')}
                    </div>
                </div>
                <div className="preview-game__body-right">
                    <div className="preview-game-team">
                        <span className="preview-game-team-span-img">
                            <GamerPhoto
                                line_gamer={lineGamer(line, GAMER_2)}
                                className="preview-game-team-img"
                            />
                        </span>
                        <span className={`preview-game-team-name ${colorClass}`}>
                            <b>{lineGamer(line, GAMER_2)?.nick_quick}</b>
                        </span>
                        <button
                            className={coefButtonClasses(GAMER_2)}
                            onClick={() => handleCoefClick(GAMER_2)}
                        >
                            {renderCoef(GAMER_2)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default QuickBetsItem;
